import { Column } from "primereact/column";
import * as React from "react";
import { Button } from "reactstrap";
import ChangeEnum from "../../interfaces/frontend/ChangeEnum";
import IResultInhibitor from "../../interfaces/frontend/IResultInhibitor";
import InhibitorEmptyMessage from "./InhibitorEmptyMessage";
import InhibitorBaseTable from "./InhibitorBaseTable";
import { ViewerLink } from "../viewer-link";
import { useCallback, useMemo } from "react";

interface Props {
  inhibitors: IResultInhibitor[],
  showOnlyAssociated: boolean,
  selected: IResultInhibitor | null,

  setSelected(inhibitor: IResultInhibitor | null): void,
  scrollToChart(): void,

  differenceFilter: ChangeEnum | null,
  setDifferenceFilter(differenceFilter: ChangeEnum | null): void,
}

function InhibitorTable({inhibitors, showOnlyAssociated, ...props}: Props) {

  const {differenceFilter} = props;

  const isSelected = useCallback((data: IResultInhibitor) => {
    return props.selected !== null && data.id === props.selected.id;
  }, [props.selected]);

  const toggleSelected = useCallback((data: IResultInhibitor) => {
    const selected = isSelected(data);
    props.setSelected(selected ? null : data);
    if (!selected) {
      // scroll
      props.scrollToChart();
    }
  }, [isSelected, props.setSelected, props.scrollToChart])

  const showBodyTemplate = useCallback((data: IResultInhibitor) => (
    <ViewerLink zincId={data.zincId}>
      <Button
        size="sm"
        title={`Show ${data.name} in the structure viewer`}
      >Show 3D</Button>
    </ViewerLink>
  ), []);

  const selectBodyTemplate = useCallback((data: IResultInhibitor) => {
    if (!data.inChart) {
      return null;
    }
    const selected = isSelected(data);
    return (
      <Button
        size="sm"
        title={`Select ${data.name} to highlight it in the chart below`}
        onClick={() => toggleSelected(data)}
        disabled={selected}
      >
        {selected ? 'Selected' : 'Select'}
      </Button>
    );
  }, [isSelected, toggleSelected]);

  const rowClassName = useCallback((data: IResultInhibitor) => (
    props.selected !== null ? {
      'p-highlight': isSelected(data),
    } : undefined
  ), [isSelected, props.selected])

  const screenInhibitors = useMemo(() => {
    let out = inhibitors;
    if (showOnlyAssociated) {
      out = out.filter(inh => inh.associatedDrug);
    }
    if (differenceFilter !== null) {
      out = out.filter(inh => inh.bindingEnergyDiffClass === differenceFilter);
    }
    return out;
  }, [inhibitors, showOnlyAssociated, differenceFilter]);

  const printInhibitors = inhibitors;

  const emptyMessage = useCallback((globalFilter: string | null) => {
    return InhibitorEmptyMessage({
      showOnlyAssociated: showOnlyAssociated,
      differenceFilter: differenceFilter,
    }, globalFilter);
  }, [showOnlyAssociated, differenceFilter]);

  return <InhibitorBaseTable
    screenInhibitors={screenInhibitors}
    printInhibitors={printInhibitors}
    emptyMessage={emptyMessage}
    dataTableProps={{
      rowClassName: rowClassName,
      cellMemo: false,  // the Select button should be rerendered after click, so we cannot memoize cells
    }}
    columnsAfter={[
      <Column key="show" body={showBodyTemplate} header="Show 3D" align="center" style={{ width: '95px' }} />,
      <Column key="select" body={selectBodyTemplate} header="Select In Chart" align="center" style={{ width: '110px' }} />,
    ]}
    {...props}
  />

}

export default InhibitorTable;
